import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import ClaimBreakdown from '../components/ClaimBreakdown';
import FinancialSummary from '../components/FinancialSummary';
import CausationTest from '../components/CausationTest';
import BundleModal from '../components/BundleModal';
import DemoScenarioProjections from '../components/DemoScenarioProjections';
import SubrogationPanel from '../components/SubrogationPanel';
import IndustryBenchmark from '../components/IndustryBenchmark';

const demoReports: { [key: string]: any } = {
  'demo-1': {
    id: 'demo-1',
    client_name: 'Acme Restaurant Group',
    claimant_name: 'John Smith',
    business_address: '123 Main Street, New York, NY 10001',
    zone: '',
    business_type: 'Restaurant',
    status: 'completed',
    created_at: '2025-11-01T10:00:00Z',
    completed_at: '2025-11-01T10:01:30Z',
    pdf_url: 'https://example.com/sample-report.pdf',
    result: {
      // REAL BI OUTPUT - MATCHES EXCEL
      final_claim_amount: 507473.68,
      step1_variable_profit_loss: 342847.21,
      step2_incremental_revenue_loss: 164626.47,
      revenue_loss: 700000,
      
      // Causation Tests
      causation_tests: {
        v_test: {
          passed: true,
          details: 'Revenue decline during coverage period significantly exceeded baseline variance (p < 0.05)',
          qualifyingPeriods: ['Apr 2020', 'May 2020', 'Jun 2020', 'Jul 2020', 'Aug 2020', 'Sep 2020']
        },
        modified_v_test: {
          passed: false,
          details: 'Modified test using alternative benchmark did not meet threshold'
        },
        down_only_test: {
          passed: true,
          details: 'All qualifying periods showed revenue decline below baseline',
          qualifyingPeriods: ['Apr-Sep 2020']
        }
      },
      
      benchmark_period: 'Jan-Apr 2021',
      
      // Financial Data - EXACT MATCH TO EXCEL
      revenue_2019: 1590000,
      revenue_2020: 1175000,
      revenue_2021: 1710000,
      revenue_2022: 1037500,
      payroll_2019: 336000,  // $28k/mo * 12
      payroll_2020: 336000,
      payroll_2021: 336000,
      payroll_2022: 336000,
      variable_profit_2019: 477000,  // 30% VP
      variable_profit_2020: 352500,  // 30% VP
      variable_profit_2021: 513000,  // 30% VP
      variable_profit_2022: 311250,  // 30% VP
      
      notes: [
        'Calculation uses 48 expense categories with granular cost allocation',
        'Benchmark period selected based on revenue stabilization patterns',
        'Step 1 represents direct variable profit loss during covered period',
        'Step 2 captures incremental losses beyond benchmark comparison'
      ]
    }
  },
  'demo-2': {
    id: 'demo-2',
    client_name: 'TechStart Industries',
    claimant_name: 'Sarah Johnson',
    business_address: '456 Tech Blvd, San Francisco, CA 94105',
    zone: '',
    business_type: 'Services',
    status: 'completed',
    created_at: '2025-10-28T14:30:00Z',
    completed_at: '2025-10-28T14:32:15Z',
    pdf_url: 'https://example.com/sample-report-2.pdf',
    result: {
      // REAL BI OUTPUT - DIFFERENT SCENARIO
      final_claim_amount: 680000.00,
      step1_variable_profit_loss: 425000.00,
      step2_incremental_revenue_loss: 255000.00,
      revenue_loss: 950000,
      
      // Causation Tests
      causation_tests: {
        v_test: {
          passed: true,
          details: 'Revenue decline during coverage period significantly exceeded baseline variance (p < 0.01)',
          qualifyingPeriods: ['Mar 2020', 'Apr 2020', 'May 2020', 'Jun 2020', 'Jul 2020', 'Aug 2020', 'Sep 2020', 'Oct 2020']
        },
        modified_v_test: {
          passed: false,
          details: 'Modified test using Q1-Q2 2020 benchmark did not meet threshold'
        },
        down_only_test: {
          passed: true,
          details: 'All qualifying periods showed revenue decline below baseline',
          qualifyingPeriods: ['Mar-Oct 2020']
        }
      },
      
      benchmark_period: 'Nov 2020-Feb 2021',
      
      // Financial Data
      revenue_2019: 2800000,
      revenue_2020: 1850000,
      revenue_2021: 2400000,
      revenue_2022: 2650000,
      payroll_2019: 1120000,
      payroll_2020: 740000,
      payroll_2021: 960000,
      payroll_2022: 1060000,
      variable_profit_2019: 896000,
      variable_profit_2020: 518000,
      variable_profit_2021: 744000,
      variable_profit_2022: 795000,
      
      notes: [
        'Tech services sector showed prolonged impact due to client contract delays',
        'Recovery pattern differs from restaurant sector due to industry dynamics',
        'Benchmark selected post-stabilization of service delivery models',
        'Higher VP% in 2019 reflects premium service pricing pre-pandemic'
      ]
    }
  },
  'demo-3': {
    id: 'demo-3',
    client_name: 'Main Street Retail',
    claimant_name: 'Mike Davis',
    business_address: '789 Commerce Ave, Chicago, IL 60601',
    zone: '',
    business_type: 'Retail',
    status: 'processing',
    created_at: '2025-11-02T09:15:00Z',
    completed_at: null,
    pdf_url: null,
    result: null
  },
  'demo-causation-failed': {
    id: 'demo-causation-failed',
    client_name: 'Gulf Coast Manufacturing',
    claimant_name: 'Maria Rodriguez',
    business_address: '456 Industrial Blvd, Mobile, AL 36601',
    zone: '',
    business_type: 'Manufacturing',
    status: 'causation_failed',
    created_at: '2025-11-03T14:20:00Z',
    completed_at: '2025-11-03T14:22:15Z',
    pdf_url: null,
    financial_data: {
      data_2018: [
        {month: 1, revenue: 85000, payroll: 25000, variable_expenses: 35000},
        {month: 2, revenue: 87000, payroll: 25000, variable_expenses: 36000},
        {month: 3, revenue: 83000, payroll: 25000, variable_expenses: 34000},
        {month: 4, revenue: 86000, payroll: 25000, variable_expenses: 35500},
        {month: 5, revenue: 88000, payroll: 25000, variable_expenses: 36500},
        {month: 6, revenue: 90000, payroll: 25000, variable_expenses: 37000},
        {month: 7, revenue: 89000, payroll: 25000, variable_expenses: 36800},
        {month: 8, revenue: 87000, payroll: 25000, variable_expenses: 36000},
        {month: 9, revenue: 85000, payroll: 25000, variable_expenses: 35000},
        {month: 10, revenue: 84000, payroll: 25000, variable_expenses: 34500},
        {month: 11, revenue: 82000, payroll: 25000, variable_expenses: 34000},
        {month: 12, revenue: 83000, payroll: 25000, variable_expenses: 34200}
      ],
      data_2019: [
        {month: 1, revenue: 84000, payroll: 25000, variable_expenses: 34500},
        {month: 2, revenue: 86000, payroll: 25000, variable_expenses: 35500},
        {month: 3, revenue: 82000, payroll: 25000, variable_expenses: 33500},
        {month: 4, revenue: 85000, payroll: 25000, variable_expenses: 35000},
        {month: 5, revenue: 87000, payroll: 25000, variable_expenses: 36000},
        {month: 6, revenue: 89000, payroll: 25000, variable_expenses: 36500},
        {month: 7, revenue: 88000, payroll: 25000, variable_expenses: 36200},
        {month: 8, revenue: 86000, payroll: 25000, variable_expenses: 35500},
        {month: 9, revenue: 84000, payroll: 25000, variable_expenses: 34500},
        {month: 10, revenue: 83000, payroll: 25000, variable_expenses: 34000},
        {month: 11, revenue: 81000, payroll: 25000, variable_expenses: 33500},
        {month: 12, revenue: 82000, payroll: 25000, variable_expenses: 33800}
      ],
      data_2020: [
        {month: 1, revenue: 83000, payroll: 25000, variable_expenses: 34000},
        {month: 2, revenue: 85000, payroll: 25000, variable_expenses: 35000},
        {month: 3, revenue: 81000, payroll: 25000, variable_expenses: 33000},
        {month: 4, revenue: 84000, payroll: 25000, variable_expenses: 34500},
        {month: 5, revenue: 86000, payroll: 25000, variable_expenses: 35500},
        {month: 6, revenue: 88000, payroll: 25000, variable_expenses: 36000},
        {month: 7, revenue: 87000, payroll: 25000, variable_expenses: 35800},
        {month: 8, revenue: 85000, payroll: 25000, variable_expenses: 35000},
        {month: 9, revenue: 83000, payroll: 25000, variable_expenses: 34000},
        {month: 10, revenue: 82000, payroll: 25000, variable_expenses: 33500},
        {month: 11, revenue: 80000, payroll: 25000, variable_expenses: 33000},
        {month: 12, revenue: 81000, payroll: 25000, variable_expenses: 33200}
      ]
    },
    result: {
      final_claim_amount: 0,
      revenue_loss: 0,
      causation_passed: false,
      causation_test_used: 'v_test',
      causation_failure_reason: 'The V-Test analysis shows insufficient variance between the benchmark period and the event period. The revenue patterns do not demonstrate a statistically significant impact from the claimed event. Consider reviewing the event dates or benchmark period selection.'
    }
  }
};

export default function DemoReportDetail() {
  const { id } = useParams();
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isDownloadingJson, setIsDownloadingJson] = useState(false);

  const report = id ? demoReports[id] : null;

  if (!report) {
    return <Navigate to="/demo" replace />;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'causation_failed':
        return 'bg-amber-100 text-amber-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDownloadExcel = async () => {
    if (isDownloadingExcel) return;
    setIsDownloadingExcel(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/audit/claim/${report.id}/export-excel`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${report.id}-forensic-workbook.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Excel download error:', error);
      toast.error('Failed to download Excel workbook. Please try again.');
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/audit/claim/${report.id}/explain-pdf`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${report.id}-explanation.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF explanation. Please try again.');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleDownloadJson = async () => {
    if (isDownloadingJson) return;
    setIsDownloadingJson(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/audit/claim/${report.id}/export`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${report.id}-audit-trail.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('JSON download error:', error);
      toast.error('Failed to download audit trail. Please try again.');
    } finally {
      setIsDownloadingJson(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Demo Banner */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded">
          <div className="flex justify-between items-center">
            <p className="text-yellow-800">
              <strong>DEMO REPORT:</strong> This is sample data for demonstration purposes.
            </p>
            <Link
              to="/sign-in"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors text-sm"
            >
              Sign In for Real Reports
            </Link>
          </div>
        </div>

        <Link
          to="/demo"
          className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center mb-6"
        >
          ← Back to Demo Dashboard
        </Link>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{report.client_name}</h1>
              <p className="text-gray-600">Claimant: {report.claimant_name}</p>
              <p className="text-sm text-gray-500">Address: {report.business_address}</p>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(report.status)}`}>
              {report.status === 'causation_failed' ? 'Causation Test Failed' : report.status}
            </span>
          </div>

          <div className="border-t pt-6 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Zone</p>
                <p className="font-semibold">{report.zone}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Business Type</p>
                <p className="font-semibold">{report.business_type}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Created</p>
                <p className="font-semibold">{new Date(report.created_at).toLocaleString()}</p>
              </div>
              {report.completed_at && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Completed</p>
                  <p className="font-semibold">{new Date(report.completed_at).toLocaleString()}</p>
                </div>
              )}
            </div>
          </div>

          {/* Causation Failed Banner - Show prominently with financial data summary */}
          {report.status === 'causation_failed' && (
            <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-amber-900 mb-2">Report Needs Attention</h3>
                  <p className="text-amber-800 mb-4">
                    {report.result?.causation_failure_reason || 'The causation test did not pass with the current data. This usually means the financial data does not show sufficient variance between the benchmark period and the event period to support a valid claim.'}
                  </p>
                  
                  {/* Show financial data summary */}
                  {report.financial_data && (
                    <div className="bg-white rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Your Uploaded Financial Data:</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {Object.keys(report.financial_data).sort().map(yearKey => {
                          const year = yearKey.replace('data_', '');
                          const yearData = report.financial_data[yearKey];
                          const totalRevenue = yearData.reduce((sum, month) => sum + (month.revenue || 0), 0);
                          return (
                            <div key={year} className="bg-gray-50 rounded-lg p-3">
                              <p className="text-xs text-gray-500 mb-1">Year {year}</p>
                              <p className="text-sm font-bold text-gray-900">
                                ${totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                              <p className="text-xs text-gray-500">{yearData.length} months</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-amber-100 rounded-lg p-4">
                    <p className="font-semibold text-amber-900 mb-2">What you can do:</p>
                    <ul className="space-y-2 text-sm text-amber-800">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>Review your benchmark period selection - you may need to choose a different time period that better represents pre-event performance</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>Verify all financial data is accurate and complete for all years</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>Check that the event dates align correctly with your coverage period</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>Ensure you've included supporting documentation to validate the claim</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="mt-4">
                    <Link 
                      to="/demo-wizard"
                      className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 px-6 rounded-lg transition"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit Report & Recalculate
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {report.result && (
            <>
              {/* Claim Breakdown - Step 1 & Step 2 */}
              <div className="mb-6">
                <ClaimBreakdown
                  step1_variable_profit_loss={report.result.step1_variable_profit_loss || 0}
                  step2_incremental_revenue_loss={report.result.step2_incremental_revenue_loss || 0}
                  final_claim_amount={report.result.final_claim_amount || 0}
                  notes={report.result.notes}
                />
              </div>

              {/* Causation Tests */}
              {report.result.causation_tests && (
                <div className="mb-6">
                  <CausationTest
                    vTest={report.result.causation_tests.v_test}
                    modifiedVTest={report.result.causation_tests.modified_v_test}
                    downOnlyTest={report.result.causation_tests.down_only_test}
                    benchmarkPeriod={report.result.benchmark_period}
                  />
                </div>
              )}

              {/* Financial Summary with Charts */}
              <div className="mb-6">
                <FinancialSummary
                  data={{
                    revenue_2019: report.result.revenue_2019,
                    revenue_2020: report.result.revenue_2020,
                    revenue_2021: report.result.revenue_2021,
                    revenue_2022: report.result.revenue_2022,
                    variable_profit_2019: report.result.variable_profit_2019,
                    variable_profit_2020: report.result.variable_profit_2020,
                    variable_profit_2021: report.result.variable_profit_2021,
                    variable_profit_2022: report.result.variable_profit_2022
                  }}
                />
              </div>

              {/* LOSS PROJECTION SCENARIOS SECTION - Feature #3 */}
              {report.status === 'completed' && (
                <div className="mb-6">
                  <DemoScenarioProjections
                    baseResult={{
                      final_claim_amount: report.result.final_claim_amount,
                      indemnity_period_days: 90,
                      daily_gross_profit: Math.round(report.result.final_claim_amount / 90)
                    }}
                  />
                </div>
              )}

              {/* INDUSTRY BENCHMARK COMPARISON - Feature #4 */}
              {report.status === 'completed' && (
                <div className="mb-6">
                  <IndustryBenchmark
                    businessType="Restaurant"
                    naicsCode="722511"
                    cogsRatio={32}
                    grossMargin={68}
                    operatingExpenseRatio={52}
                    netProfitMargin={8}
                    hasReconstructedData={false}
                  />
                </div>
              )}
            </>
          )}

          {report.status === 'processing' || report.status === 'pending' ? (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                <div>
                  <p className="font-semibold text-blue-800">PDF generating...</p>
                  <p className="text-sm text-blue-600">This is a demo - real reports take 8-15 seconds</p>
                </div>
              </div>
            </div>
          ) : report.pdf_url ? (
            <div className="space-y-6">
              {/* PDF Download Options Header */}
              <div>
                <h2 className="text-2xl font-bold mb-2">📄 Download Options</h2>
                <p className="text-gray-600">Choose the report format that best fits your needs (Sign in to activate)</p>
              </div>

              {/* Three Download Options */}
              <div className="grid md:grid-cols-3 gap-6">
                
                {/* Option A: Standard Standard BI Report (Free) */}
                <div className="border-2 border-gray-300 rounded-xl p-6 hover:border-blue-500 transition">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">FREE</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Standard Report</h3>
                  <p className="text-gray-600 text-sm mb-4">11-page BI calculation with charts, tables, and methodology</p>
                  <ul className="text-sm text-gray-600 space-y-1 mb-4">
                    <li>✓ Calculation breakdown</li>
                    <li>✓ Statistical validation</li>
                    <li>✓ Court-ready format</li>
                  </ul>
                  <Link
                    to="/sign-in"
                    className="w-full inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg shadow transition"
                  >
                    Sign In to Download
                  </Link>
                </div>

                {/* Option B: AI Narrative Report ($150) */}
                <div className="border-2 border-blue-500 rounded-xl p-6 relative overflow-hidden shadow-lg">
                  <div className="absolute top-0 right-0 bg-gradient-to-br from-blue-500 to-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    PREMIUM
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </div>
                    <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-bold">$150</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">AI Narrative</h3>
                  <p className="text-gray-600 text-sm mb-4">14-page AI-written legal narrative for expert witness testimony</p>
                  <ul className="text-sm text-gray-600 space-y-1 mb-4">
                    <li>✨ Executive summary</li>
                    <li>✨ Causation analysis</li>
                    <li>✨ Professional attestation</li>
                  </ul>
                  <Link
                    to="/demo-narrator"
                    className="w-full inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 text-white font-bold py-3 px-4 rounded-lg shadow transition"
                  >
                    Try Demo Narrator →
                  </Link>
                  <p className="text-xs text-gray-500 mt-2 text-center">Save 4 hours of writing time</p>
                </div>

                {/* Option C: Complete Bundle ($200) */}
                <div className="border-2 border-blue-500 rounded-xl p-6 bg-gradient-to-br from-blue-50 to-blue-50">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-bold">$200</span>
                    <span className="bg-yellow-400 text-yellow-900 px-2 py-1 rounded text-xs font-bold">SAVE $50</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Complete Bundle</h3>
                  <p className="text-gray-600 text-sm mb-4">25-page comprehensive package with calculations + AI narrative</p>
                  <ul className="text-sm text-gray-600 space-y-1 mb-4">
                    <li>📊 All calculations & charts</li>
                    <li>📝 Full legal narrative</li>
                    <li>⚖️ Complete court submission</li>
                  </ul>
                  <button
                    onClick={() => setShowBundleModal(true)}
                    className="w-full inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg shadow transition"
                    aria-label="Get Complete Bundle - Standard Report plus AI Narrative for $200"
                  >
                    Get Complete Bundle
                  </button>
                  <p className="text-xs text-gray-500 mt-2 text-center">Best value for serious claims</p>
                </div>
              </div>

              {/* Bundle Modal */}
              <BundleModal
                isOpen={showBundleModal}
                onClose={() => setShowBundleModal(false)}
                claimId={report.id}
                claimData={{
                  client_name: report.client_name,
                  total_claim: report.result?.final_claim_amount || 0,
                  event_type: report.business_type
                }}
                demoMode={true}
              />

              {/* Forensic Package Section (v1.3.2) */}
              <div className="mt-8 pt-8 border-t-2 border-gray-200">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold mb-2">🏛️ Forensic Verification Package</h2>
                  <p className="text-gray-600">Court-defensible documentation for accountants and legal review</p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  
                  {/* Option 1: Excel Export */}
                  <div className="border-2 border-green-300 rounded-xl p-6 hover:border-green-500 transition bg-green-50">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <span className="bg-green-600 text-white px-2 py-1 rounded text-xs font-bold">EXCEL</span>
                    </div>
                    <h3 className="text-lg font-bold mb-2">Forensic Workbook</h3>
                    <p className="text-gray-700 text-sm mb-3">8-sheet Excel with all calculations, formulas, and audit trail</p>
                    <ul className="text-xs text-gray-700 space-y-1 mb-4">
                      <li>✓ 48-month data input</li>
                      <li>✓ Variable profit formulas</li>
                      <li>✓ 156 benchmark candidates</li>
                      <li>✓ Complete audit trail</li>
                    </ul>
                    <button
                      onClick={handleDownloadExcel}
                      disabled={isDownloadingExcel}
                      className="w-full inline-flex items-center justify-center bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg shadow transition text-sm"
                      aria-label="Download Forensic Workbook as Excel"
                    >
                      {isDownloadingExcel ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                          Downloading...
                        </>
                      ) : (
                        '📥 Download Excel'
                      )}
                    </button>
                  </div>

                  {/* Option 2: AI Explanation PDF */}
                  <div className="border-2 border-blue-300 rounded-xl p-6 hover:border-blue-500 transition bg-blue-50">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-bold">PDF</span>
                    </div>
                    <h3 className="text-lg font-bold mb-2">AI Explanation</h3>
                    <p className="text-gray-700 text-sm mb-3">2-page plain English summary for clients and stakeholders</p>
                    <ul className="text-xs text-gray-700 space-y-1 mb-4">
                      <li>✓ Executive summary</li>
                      <li>✓ Calculations explained</li>
                      <li>✓ AI decisions detailed</li>
                      <li>✓ BI methodology</li>
                    </ul>
                    <button
                      onClick={handleDownloadPdf}
                      disabled={isDownloadingPdf}
                      className="w-full inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg shadow transition text-sm"
                      aria-label="Download AI Explanation as PDF"
                    >
                      {isDownloadingPdf ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                          Downloading...
                        </>
                      ) : (
                        '📥 Download Explanation'
                      )}
                    </button>
                  </div>

                  {/* Option 3: Audit Trail JSON */}
                  <div className="border-2 border-blue-300 rounded-xl p-6 hover:border-blue-500 transition bg-blue-50">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-bold">JSON</span>
                    </div>
                    <h3 className="text-lg font-bold mb-2">Audit Trail</h3>
                    <p className="text-gray-700 text-sm mb-3">Complete JSON export of all changes and AI decisions</p>
                    <ul className="text-xs text-gray-700 space-y-1 mb-4">
                      <li>✓ 100% coverage</li>
                      <li>✓ Field-level changes</li>
                      <li>✓ AI decision log</li>
                      <li>✓ System events</li>
                    </ul>
                    <button
                      onClick={handleDownloadJson}
                      disabled={isDownloadingJson}
                      className="w-full inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg shadow transition text-sm"
                      aria-label="Download Audit Trail as JSON"
                    >
                      {isDownloadingJson ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                          Downloading...
                        </>
                      ) : (
                        '📥 Download Audit Trail'
                      )}
                    </button>
                  </div>
                </div>

                {/* Forensic Package Info */}
                <div className="mt-6 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-300 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Court-Defensible Verification</p>
                      <p className="text-sm text-gray-700">
                        All forensic outputs are available for download. These materials allow forensic accountants 
                        to verify every calculation, trace all data changes, and understand AI-assisted decisions. 
                        Perfect for legal review and expert testimony preparation.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Demo Notice */}
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mt-6">
                <p className="text-sm text-yellow-800">
                  <strong>🔒 Demo Mode:</strong> Sign in to unlock PDF downloads and AI narrative generation. 
                  Create your account to access all premium features.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-600">PDF not yet available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
